/**
 * 原料自检清单(IA 设计稿 §3.1/§3.2「Dock 专属」;2026-08-08 P6)。
 *
 * ★为什么是**清单**不是**校验**:真正判「这张脸能不能用」的是上游 face check,
 *   console 侧没有独立端点(设计稿 §四已挂账)。前端能确定的只有类型 / 大小 ——
 *   那两条已经在上传口硬拦了。剩下的「正脸 / 单人 / 光线 / 别遮脸」只能**先说清**。
 *   ★所以这里一个字都不写成「已通过检测」——我们没检测,我们只是把要求讲在前面。
 *   (存量教训:说得斩钉截铁却不准,比不说更糟。)
 *
 * ★还写清**失败会怎样**:上游 face check 不过会回 `IMAGE_CHECK_FAILED`,任务直接失败。
 *   用户提前知道「不合规是出不了片、而不是出一条丑的」,才愿意花十秒换张照片。
 */
import { CircleAlert } from "lucide-react";

import { DH_VOICE_SAMPLE_RULE } from "../../model/dhConsentText";

export interface DhMaterialChecklistProps {
  /** video = 真人口播线的源视频自检;photo = 照片线的照片体检。 */
  kind: "video" | "photo";
  maxVideoMb: number;
  /** 源视频最长时长文案(按出片通道取,见 model/dhVideoLimits);缺省老口径「2 分钟」。 */
  maxVideoDurationLabel?: string;
}

const VIDEO_ITEMS = [
  "mp4 / mov,单个不超过 {MB}MB",
  "10~15 秒最稳(口型最准);最长 {MAXDUR}",
  "画面里只有你一个人,正脸对镜头、别侧太多",
  "**带声音** —— 想用原声念新文案,靠的就是这条音轨",
  // ★2026-08-13 实测:「用原声」= 拿这条音轨复刻音色,而复刻音色会把样本内容念进成片。
  //   有人拿「数 1 到 10」当底板,出片就多出「8 9 10 1 2 3 4 5 6 7」。与声音库上传样本同一句规矩。
  `**说一段完整的话** —— ${DH_VOICE_SAMPLE_RULE}`,
  "光线均匀、背景别太乱;别戴口罩墨镜、别用手挡脸",
];

const PHOTO_ITEMS = [
  "png / jpeg / webp 的清晰单人正面照",
  "只有一张脸:合影会让引擎挑错人",
  "五官别被遮住 —— 口罩、墨镜、刘海压眼都不行",
  "光线均匀,别逆光、别过曝到看不清轮廓",
  "别用大角度侧脸、低头仰头的照片",
];

/** 把 `**xx**` 渲染成强调(清单里只有这一处需要,不引 markdown)。 */
function Line({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <b key={i} className="font-medium text-foreground">
            {p}
          </b>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

export function DhMaterialChecklist({
  kind,
  maxVideoMb,
  maxVideoDurationLabel = "2 分钟",
}: DhMaterialChecklistProps) {
  const items = (kind === "video" ? VIDEO_ITEMS : PHOTO_ITEMS).map((t) =>
    t.replace("{MB}", String(maxVideoMb)).replace("{MAXDUR}", maxVideoDurationLabel)
  );

  return (
    <div
      data-testid={`dh-material-checklist-${kind}`}
      className="space-y-1 rounded-md border border-border bg-muted/20 p-2"
    >
      <p className="text-2xs font-medium">
        {kind === "video" ? "源视频怎么拍最稳" : "照片怎么选最稳"}
      </p>
      <ul className="space-y-0.5 text-2xs leading-relaxed text-muted-foreground">
        {items.map((t) => (
          <li key={t} className="flex gap-1.5">
            <span aria-hidden className="select-none">
              ·
            </span>
            <span>
              <Line text={t} />
            </span>
          </li>
        ))}
      </ul>
      {/* ★把「不合规会怎样」说明白:是**出不了片**,不是出一条丑的 */}
      <p className="flex gap-1 pt-0.5 text-2xs text-amber-600 dark:text-amber-500">
        <CircleAlert className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          这几条是给引擎看的:不合规会在人脸检测那一步直接失败(出不了片),不是出一条效果差的。
        </span>
      </p>
    </div>
  );
}
